import { Button } from "@dropbox/dig-components/buttons";
import { Text } from "@dropbox/dig-components/typography";
import { getTimeString } from "@mirage/mosaics/Chat/utils/time";
import { ChatDiagnosticsViewerNode } from "@mirage/mosaics/ConversationAssistant/containers/ConversationAssistantPageContent/diagnostics/ChatDiagnosticsViewerNode";
import { ChatDiagnosticsViewerProcess } from "@mirage/mosaics/ConversationAssistant/containers/ConversationAssistantPageContent/diagnostics/ChatDiagnosticsViewerProcess";
import type {
  ConversationsResult,
  ConversationsResultError,
  ConversationsResultSuccess,
} from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/types";
import { memo, useState } from "react";

interface ChatDiagnosticsViewerResultProps {
  result: ConversationsResult;
}
export const ChatDiagnosticsViewerResult = memo(
  ({ result }: ChatDiagnosticsViewerResultProps) => {
    if ("error" in result) {
      return <ChatDiagnosticsViewerResultError result={result} />;
    } else {
      return <ChatDiagnosticsViewerResultSuccess result={result} />;
    }
  },
);
ChatDiagnosticsViewerResult.displayName = "ChatDiagnosticsViewerResult";

interface ChatDiagnosticsViewerResultSuccessProps {
  result: ConversationsResultSuccess;
}
export const ChatDiagnosticsViewerResultSuccess = memo(
  ({ result }: ChatDiagnosticsViewerResultSuccessProps) => {
    const [expandedAll, setExpandedAll] = useState(false);
    const timeString = getTimeString(new Date(result.timestamp).getTime(), {
      hidePrefix: true,
    });
    const diagnostics = result.unprocessedResponse?.diagnostics;
    const expandToggle = (
      <Button
        variant="opacity"
        size="small"
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
          setExpandedAll(!expandedAll);
          e.stopPropagation();
        }}
      >
        {expandedAll ? "Collapse All" : "Expand All"}
      </Button>
    );
    return (
      <ChatDiagnosticsViewerNode
        title={timeString}
        subheader="Success"
        rightAccessory={expandToggle}
        defaultExpanded={expandedAll}
      >
        {diagnostics === undefined
          ? "No diagnostics"
          : diagnostics.processes.map((process, i) => (
              <ChatDiagnosticsViewerProcess
                key={i}
                process={process}
                diagnostics={diagnostics}
                expandedAll={expandedAll}
              />
            ))}
      </ChatDiagnosticsViewerNode>
    );
  },
);
ChatDiagnosticsViewerResultSuccess.displayName =
  "ChatDiagnosticsViewerResultSuccess";

interface ChatDiagnosticsViewerResultErrorProps {
  result: ConversationsResultError;
}
export const ChatDiagnosticsViewerResultError = memo(
  ({ result }: ChatDiagnosticsViewerResultErrorProps) => {
    const timeString = getTimeString(new Date(result.timestamp).getTime());
    return (
      <ChatDiagnosticsViewerNode title={timeString} subheader="Error">
        <Text color="error" tagName="div">
          {result.error}
        </Text>
        <Text tagName="div">
          <i>Request ID:</i> {result.requestId}
        </Text>
      </ChatDiagnosticsViewerNode>
    );
  },
);
ChatDiagnosticsViewerResultError.displayName =
  "ChatDiagnosticsViewerResultError";
